/**
 * Uma organização no detalhe da gestão (#765, fase 3, #767).
 *
 * ── O formato que a tela usa ──────────────────────────────────────────────
 *
 * A tela nasceu antes do back (decisão de 26/10/2026) com este formato, e o
 * contrato de `GET /v1/operacao/organizacoes/{id}` traz os mesmos dados com os
 * nomes do `/v1`. `organizacaoDetalhada` só troca os nomes. O histórico já usa
 * os tipos do contrato.
 *
 * O cadastro da empresa (razão social, CNPJ, segmento) e o contato financeiro
 * nascem com a criação da organização (#769). A organização de antes vem com
 * eles vazios, e a tela diz "Não informado" até alguém preencher.
 *
 * O contrato guarda os valores DESTE cliente: eles vieram do plano no dia da
 * contratação e podem ser ajustados só para ele. O plano vem junto para a tela
 * dizer quando um valor é personalizado.
 */

import {
  BYTES_POR_GB,
  SEGMENTOS_DA_ORGANIZACAO,
  type EntradaDoHistoricoDaOrganizacao,
  type EventoDaOrganizacao as EventoDaApi,
  type MudancaNoCadastroDaOrganizacao,
  type MudancaNoContratoDaOrganizacao,
  type OrganizacaoDetalhada as OrganizacaoDaApi,
  type ParteDaCriacao,
  type SegmentoDaOrganizacao,
} from '@cliqcrm/contracts';
import { FUSOS_DO_BRASIL } from '@/shared/lib/fusos-do-brasil';

/** Dinheiro como o contrato manda: micros, para centavo não acumular erro. */
export type Dinheiro = { readonly quantia_micros: number; readonly moeda: string };

export type SituacaoDaOrganizacao = 'ativa' | 'em_criacao' | 'suspensa' | 'encerrada';

/** Quem a equipe procura para a cobrança. Dado pessoal: só nesta tela, e nunca em log. */
export type ContatoFinanceiro = {
  readonly nome: string;
  readonly email: string;
  /** No formato internacional: "+5511987654321". */
  readonly telefone: string;
};

/** Um segmento, com o valor que vai ao servidor e o nome que a tela mostra. */
export type Segmento = { readonly valor: SegmentoDaOrganizacao; readonly nome: string };

export type ContratoDaOrganizacao = {
  /** O início do contrato, em ISO. */
  readonly inicio: string;
  readonly mensalidade: Dinheiro | null;
  readonly usuariosContratados: number;
  readonly usuarioAdicional: Dinheiro | null;
  readonly armazenamentoContratadoGb: number;
  readonly gbAdicional: Dinheiro | null;
  /** Quem a equipe procura para a cobrança, guardado com o contrato. */
  readonly contatoFinanceiro: ContatoFinanceiro | null;
};

export type OrganizacaoDetalhada = {
  readonly id: string;
  readonly nome: string;
  readonly identificador: string;
  readonly situacao: SituacaoDaOrganizacao;
  /** Quando a organização foi criada, em ISO. */
  readonly desde: string;
  /** Só em criação: a parte onde a criação parou ("Atendimento"). */
  readonly parouEm: string | null;
  /**
   * A exclusão de vez, quando houve: a data, até quando o que o CliqCRM guarda
   * dela deve sair (a purga, por enquanto manual), e se alguma parte não
   * terminou, para excluir de novo.
   */
  readonly exclusao: {
    readonly em: string;
    readonly purgaAte: string;
    readonly incompleta: boolean;
    /** As partes que não terminaram, com o nome da tela ("Atendimento"). */
    readonly partesPendentes: readonly string[];
    /** O que ficou para alguém fazer depois, como cada tentativa gravou. */
    readonly pendencias: readonly string[];
    /** A conta do atendimento ficou de propósito; o que fazer com ela está nas pendências. */
    readonly contaDoAtendimentoFicou: boolean;
  } | null;
  /** A organização da operação da Cliqfy: não se encerra nem se exclui pela gestão. */
  readonly daOperacao: boolean;
  readonly cadastro: {
    readonly razaoSocial: string | null;
    /** Sem a máscara (a tela a põe), com as letras do CNPJ alfanumérico. */
    readonly cnpj: string | null;
    readonly segmento: Segmento | null;
    /** O fuso em palavras: "Brasília (America/Sao_Paulo)". */
    readonly fuso: string;
  };
  /** O plano do contrato, com os preços padrão dele. */
  readonly plano: {
    readonly nome: string;
    readonly usuarioAdicional: Dinheiro | null;
    readonly gbAdicional: Dinheiro | null;
  } | null;
  readonly contrato: ContratoDaOrganizacao | null;
  /**
   * Na excluída, o contato financeiro não aparece, mas fica guardado até a
   * purga: verdadeiro enquanto ele existe, para a tela oferecer apagá-lo a
   * pedido. Falso fora da excluída.
   */
  readonly contatoAteAPurga: boolean;
  /**
   * Na organização em criação, até quando o pedido da criação fica guardado
   * para o "Retomar", em ISO; nulo sem pedido guardado, ou fora da criação.
   */
  readonly pedidoGuardadoAte: string | null;
  readonly uso: {
    readonly usuariosEmUso: number;
    /** Em GB; nulo enquanto a organização não foi medida (#716). */
    readonly armazenamentoGb: number | null;
    /**
     * Em GB: os anexos que aparecem no uso e não entram na cota, da conta do
     * Atendimento dividida com outra organização. Zero quando nada fica de fora.
     */
    readonly armazenamentoForaDaCotaGb: number;
    /**
     * A conta do Atendimento é ligada também a outra organização: as duas
     * recebem as conversas e os contatos uma da outra (#1161).
     */
    readonly contaDoAtendimentoDividida: boolean;
  };
  readonly administradores: readonly {
    readonly id: string;
    readonly nome: string;
    readonly email: string;
    /** O do cadastro da pessoa, no formato internacional; nulo quando ela não tem. */
    readonly telefone: string | null;
  }[];
};

/** Uma mudança no contrato, com antes e depois, como o histórico a guarda. */
export type MudancaNoContrato = MudancaNoContratoDaOrganizacao;

/** Uma mudança no cadastro: a empresa com antes e depois, o contato só como incluído, trocado ou removido. */
export type MudancaNoCadastro = MudancaNoCadastroDaOrganizacao;

export type EventoDaOrganizacao = EventoDaApi;

export type EntradaDoHistorico = EntradaDoHistoricoDaOrganizacao;

/** O que a edição do cadastro pede ao servidor: só o que mudou. */
export type MudancaDoCadastro = {
  readonly razaoSocial?: string;
  /** Sem a máscara, com as letras do CNPJ alfanumérico. */
  readonly cnpj?: string;
  readonly segmento?: SegmentoDaOrganizacao;
  /**
   * Nulo apaga o contato: é o caminho de quem pede para sair do cadastro. O
   * telefone vai como a pessoa digitou: o servidor o grava no formato
   * internacional.
   */
  readonly contatoFinanceiro?: ContatoFinanceiro | null;
};

/** Os segmentos, com o nome da tela, na ordem da escolha. */
export const SEGMENTOS: readonly Segmento[] = SEGMENTOS_DA_ORGANIZACAO;

/** O segmento pelo valor que o servidor mandou. */
export function segmentoDe(valor: SegmentoDaOrganizacao | null): Segmento | null {
  return SEGMENTOS.find((segmento) => segmento.valor === valor) ?? null;
}

/** O que a edição do contrato pede ao servidor. */
export type MudancaDoContrato = {
  /** Só o que a pessoa mudou: o campo que ficou como estava não vai. */
  readonly usuariosContratados?: number;
  readonly usuarioAdicional?: Dinheiro;
  /** O armazenamento, que o Super Admin muda pela gestão (#718). */
  readonly armazenamentoContratadoGb?: number;
  readonly gbAdicional?: Dinheiro;
};

const PARTES_DA_CRIACAO: Readonly<Record<ParteDaCriacao, string>> = {
  acesso: 'Acesso',
  cadastro: 'Cadastro',
  atendimento: 'Atendimento',
  administrador: 'Administrador',
};

/** As partes de uma exclusão, com o nome da tela. */
const PARTES_DA_EXCLUSAO: Readonly<
  Record<NonNullable<OrganizacaoDaApi['exclusao']>['partes_pendentes'][number], string>
> = {
  acesso: 'Acesso',
  atendimento: 'Atendimento',
  conexoes: 'Conexões guardadas no CliqCRM',
};

/** O fuso em palavras: "Brasília (America/Sao_Paulo)"; o que não está na lista, como veio. */
export function fusoEmPalavras(fuso: string): string {
  return FUSOS_DO_BRASIL.find((opcao) => opcao.valor === fuso)?.rotulo ?? fuso;
}

/**
 * O início do contrato é um DIA ("2026-11-01"). Lido como instante, ele seria
 * a meia-noite em UTC, que no Brasil ainda é o dia anterior. Ao meio-dia em
 * UTC, o dia é o mesmo em todos os fusos do país.
 */
const diaComoInstante = (dia: string): string => `${dia}T12:00:00.000Z`;

/** A organização do contrato da API, com os nomes que a tela usa. */
export function organizacaoDetalhada(organizacao: OrganizacaoDaApi): OrganizacaoDetalhada {
  const { contrato, plano } = organizacao;
  return {
    id: organizacao.id,
    nome: organizacao.nome,
    identificador: organizacao.identificador,
    situacao: organizacao.situacao,
    desde: organizacao.desde,
    parouEm: organizacao.parou_em ? PARTES_DA_CRIACAO[organizacao.parou_em] : null,
    exclusao: organizacao.exclusao
      ? {
          em: organizacao.exclusao.em,
          purgaAte: organizacao.exclusao.purga_ate,
          incompleta: organizacao.exclusao.incompleta,
          partesPendentes: organizacao.exclusao.partes_pendentes.map(
            (parte) => PARTES_DA_EXCLUSAO[parte],
          ),
          pendencias: organizacao.exclusao.pendencias,
          contaDoAtendimentoFicou: organizacao.exclusao.conta_do_atendimento_ficou,
        }
      : null,
    daOperacao: organizacao.da_operacao,
    cadastro: {
      razaoSocial: organizacao.cadastro.razao_social,
      cnpj: organizacao.cadastro.cnpj,
      segmento: segmentoDe(organizacao.cadastro.segmento),
      fuso: fusoEmPalavras(organizacao.fuso),
    },
    plano: plano
      ? {
          nome: plano.nome,
          usuarioAdicional: plano.usuario_adicional,
          gbAdicional: plano.gb_adicional,
        }
      : null,
    contrato: contrato
      ? {
          inicio: diaComoInstante(contrato.inicio),
          mensalidade: contrato.mensalidade,
          usuariosContratados: contrato.usuarios_contratados,
          usuarioAdicional: contrato.usuario_adicional,
          armazenamentoContratadoGb: contrato.armazenamento_gb,
          gbAdicional: contrato.gb_adicional,
          contatoFinanceiro: organizacao.contato_financeiro,
        }
      : null,
    contatoAteAPurga: organizacao.contato_financeiro_ate_a_purga,
    pedidoGuardadoAte: organizacao.pedido_guardado_ate,
    // Antes da primeira medição (#716), o armazenamento em uso é nulo.
    uso: {
      usuariosEmUso: organizacao.uso.usuarios_em_uso,
      armazenamentoGb:
        organizacao.uso.armazenamento_em_uso_bytes === null
          ? null
          : organizacao.uso.armazenamento_em_uso_bytes / BYTES_POR_GB,
      armazenamentoForaDaCotaGb: organizacao.uso.armazenamento_fora_da_cota_bytes / BYTES_POR_GB,
      contaDoAtendimentoDividida: organizacao.uso.conta_do_atendimento_dividida,
    },
    administradores: organizacao.administradores,
  };
}
